"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "../../atoms/button/button";
import styles from "./marquee-media.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/** The block's client half: the two glass controls the frames draw over the
 *  scrim, and nothing else. "use client" is earned because THERE IS NO
 *  ZERO-JAVASCRIPT FORM OF IT — play(), pause() and .muted are reachable only
 *  from script, and the one script-free alternative, a `controls` attribute, is
 *  UA chrome rather than these two controls. Nothing here is measured, nothing
 *  is reported, and both controls are real buttons. */

/** Named by the ACTION each press performs, so the glyph and the name can never
 *  disagree — each glyph is the same action drawn. No frame publishes any
 *  wording, so it is the block's own. */
export const PAUSE_LABEL = "Pause the video";
export const PLAY_LABEL = "Play the video";
export const MUTE_LABEL = "Mute the video";
export const UNMUTE_LABEL = "Unmute the video";

/** The marks the block writes and the controls read. ⚠ MIRRORED BY HAND ON BOTH
 *  SIDES OF THE BOUNDARY AND NEVER IMPORTED ACROSS IT: importing a constant out
 *  of this module into a Server Component hands the server a client reference.
 *  The spec imports both sides and asserts the two still meet. */
const ROOT = "[data-mm-root]";
const MEDIA = "[data-mm-media]";

/** The setting that says the film should not open by itself. */
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** useLayoutEffect in the browser and useEffect on the server, where the first
 *  warns. The motion setting's answer has to land in the same commit as the
 *  insert, or a reader who asked for less motion sees a frame of film. */
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

/** The film this row acts on, found by walking up to the block's own root — a
 *  real scope, so two blocks on one page cannot cross-wire. */
function mediaFor(host: HTMLElement | null): HTMLMediaElement | null {
  const root = host?.closest(ROOT);

  return root ? root.querySelector<HTMLMediaElement>(MEDIA) : null;
}

/** The pause mark, drawn while the film is running. */
function PauseGlyph() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="100%"
      viewBox="0 0 20 20"
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.55556 4.72222C6.55556 4.26198 6.92865 3.88889 7.38889 3.88889C7.84913 3.88889 8.22222 4.26198 8.22222 4.72222V15.2778C8.22222 15.738 7.84913 16.1111 7.38889 16.1111C6.92865 16.1111 6.55556 15.738 6.55556 15.2778V4.72222Z"
        fill="currentColor"
      />
      <path
        d="M11.7778 4.72222C11.7778 4.26198 12.1509 3.88889 12.6111 3.88889C13.0713 3.88889 13.4444 4.26198 13.4444 4.72222V15.2778C13.4444 15.738 13.0713 16.1111 12.6111 16.1111C12.1509 16.1111 11.7778 15.738 11.7778 15.2778V4.72222Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** The play mark, drawn once the film is stopped. ⚠ A 28 VIEWBOX WHERE THE
 *  PAUSE MARK IS 20 — the two states are published on different frames and the
 *  icon set draws them at different insets. Both fill the control's own box. */
function PlayGlyph() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="100%"
      viewBox="0 0 28 28"
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.22222 7.96839C6.22222 6.46074 7.33325 5.79829 8.70068 6.50644L21.5358 13.1157C22.8955 13.8163 22.8955 14.966 21.5358 15.6666L8.70068 22.2759C7.34102 22.9764 6.22222 22.3139 6.22222 20.8139V7.96839Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** The sound mark drawn while the film is MUTED: the press turns sound on, and
 *  every glyph here is the action rather than the state. */
function SoundOnGlyph() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="100%"
      viewBox="0 0 20 20"
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.03801 4.74264V15.8061C9.03801 15.9794 8.94435 16.1414 8.79009 16.2196C8.72398 16.2532 8.65236 16.2699 8.58074 16.2699C8.48708 16.2699 8.39343 16.242 8.3163 16.1861C8.3163 16.1861 4.41578 12.6547 4.33315 12.5765H2.30024C1.95316 12.5765 1.66667 12.2915 1.66667 11.9339V8.71544C1.66667 8.36342 1.95316 8.07288 2.30024 8.07288H4.32212C4.39374 8.01142 8.30528 4.36269 8.30528 4.36269C8.3824 4.30122 8.47606 4.2733 8.57522 4.2733C8.64684 4.2733 8.71846 4.29004 8.78458 4.32357C8.93883 4.4018 9.0325 4.56383 9.0325 4.73705L9.03801 4.74264ZM15.594 16.432C17.401 14.1969 18.1392 11.2914 17.6104 8.45284C17.3184 6.86595 16.6352 5.36847 15.6381 4.12242C15.4232 3.85422 15.0321 3.80953 14.7676 4.02744C14.5032 4.24536 14.4591 4.64206 14.6739 4.91026C15.5389 5.99427 16.1339 7.30178 16.3873 8.68751C16.8446 11.1572 16.2055 13.694 14.6299 15.6385C14.415 15.9067 14.4536 16.3035 14.718 16.5214C14.8337 16.6164 14.9715 16.6667 15.1092 16.6667C15.291 16.6667 15.4673 16.5884 15.5885 16.4376L15.594 16.432ZM13.7484 14.806C14.999 13.1241 15.5003 10.9729 15.1147 8.91103C14.8998 7.73763 14.426 6.67039 13.7098 5.73166C13.5005 5.45787 13.1093 5.40757 12.8394 5.6199C12.5694 5.83223 12.5198 6.22897 12.7292 6.50276C13.3242 7.27944 13.7153 8.16786 13.8916 9.1457C14.2112 10.8611 13.7925 12.6492 12.7512 14.0517C12.5474 14.331 12.597 14.7221 12.8724 14.9345C12.9826 15.0183 13.1148 15.063 13.2471 15.063C13.4344 15.063 13.6217 14.9792 13.7429 14.8116L13.7484 14.806ZM11.7816 13.2303C12.6025 12.1351 12.9275 10.7326 12.6796 9.38037C12.5364 8.61486 12.2334 7.92199 11.7816 7.31294C11.5722 7.03915 11.1866 6.98329 10.9112 7.19562C10.6412 7.40795 10.5861 7.79909 10.7955 8.07848C11.1315 8.52549 11.3574 9.03954 11.4621 9.60948C11.6494 10.6153 11.407 11.6546 10.7955 12.4703C10.5861 12.7497 10.6412 13.1409 10.9166 13.3532C11.0268 13.437 11.1591 13.4817 11.2913 13.4817C11.4786 13.4817 11.6659 13.3979 11.7871 13.2303H11.7816Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** The sound mark drawn while the film is AUDIBLE, on the same reading: the
 *  press turns sound off. Published on 10219:24977, at a 28 viewBox. */
function SoundOffGlyph() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="100%"
      viewBox="0 0 28 28"
      width="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.16778 10.924L14.8087 17.5681V21.6993C14.8087 21.9405 14.6765 22.1661 14.4588 22.275C14.3655 22.3217 14.2644 22.345 14.1633 22.345C14.0311 22.345 13.8989 22.3062 13.79 22.2284C13.79 22.2284 8.28443 17.3114 8.16778 17.2025H5.29834C4.80844 17.2025 4.40406 16.8057 4.40406 16.3077V11.8265C4.40406 11.3363 4.80844 10.9318 5.29834 10.9318H8.15224C8.15224 10.9318 8.16001 10.924 8.16778 10.9162V10.924ZM23.8681 22.7807L21.4263 20.3378C21.4263 20.3378 21.4497 20.3222 21.4575 20.3067C23.2227 17.9649 23.9303 14.9696 23.3937 12.0988C23.0905 10.465 22.4217 8.97897 21.4108 7.67193C21.1153 7.29071 20.5632 7.22068 20.1821 7.51632C19.8011 7.81196 19.7311 8.36435 20.0266 8.74557C20.8665 9.827 21.4186 11.064 21.6752 12.4255C22.1107 14.7517 21.5663 17.1635 20.1821 19.093L18.8913 17.8015C19.8866 16.3233 20.2755 14.5106 19.9411 12.7523C19.7389 11.6864 19.3112 10.7217 18.6735 9.88147C18.378 9.50025 17.8337 9.42244 17.4449 9.71808C17.0639 10.0137 16.9861 10.5583 17.2816 10.9473C17.7559 11.5697 18.0748 12.2855 18.2225 13.0791C18.448 14.2694 18.2225 15.4986 17.6237 16.5412L14.8087 13.7248V6.30264C14.8087 6.05368 14.6765 5.83584 14.4588 5.72692C14.3655 5.68024 14.2644 5.65688 14.1633 5.65688C14.0233 5.65688 13.8989 5.69581 13.7823 5.78139C13.7823 5.78139 11.7915 7.60969 10.1818 9.0879L5.2128 4.11645C4.90953 3.81303 4.41962 3.81303 4.11635 4.11645C3.81307 4.41988 3.81307 4.91004 4.11635 5.21346L22.7794 23.8855C22.9349 24.0411 23.1294 24.1111 23.3315 24.1111C23.5337 24.1111 23.7281 24.0333 23.8837 23.8855C24.1869 23.5821 24.1869 23.0919 23.8837 22.7885L23.8681 22.7807Z"
        fill="currentColor"
      />
    </svg>
  );
}

export interface MediaControlsProps {
  /** Draw the sound control beside the playback one. The block decides this
   *  from its own field, so no CMS value crosses the boundary. */
  showMute: boolean;
}

function MediaControlsImpl({ showMute }: MediaControlsProps) {
  /* EVERY ONE OF THESE HAS A VALUE THE SERVER CAN ANSWER, which is what lets
     both control names be identical on both sides with no `mounted` flag. */

  /* Whether the film is meant to be running. True, because the element carries
     autoPlay and no server can read the motion setting. */
  const [wanted, setWanted] = useState(true);
  const [muted, setMuted] = useState(true);
  /* A pause the block did not cause — Low Power Mode, a media key,
     Picture-in-Picture, another tab taking audio focus. */
  const [interrupted, setInterrupted] = useState(false);

  const hostRef = useRef<HTMLDivElement>(null);

  /* The pause the block itself asked for, so the listener below can tell it
     from an interruption. A ref rather than state: it is set and read inside
     one task, and a render would arrive too late. */
  const selfPaused = useRef(false);

  /* And the mirror of it, for the film starting again. Without a claim on this
     side the block cannot tell its OWN play() from a resume it did not ask for,
     and every one of them would read the same way. */
  const selfPlayed = useRef(false);

  const playing = wanted && !interrupted;

  /* THE MOTION SETTING, read in a layout effect so the pause lands in the same
     commit as the insert. ⚠ autoPlay IS NOT GATED ON IT: no server can read a
     media query, so gating rendered output on one is a hydration mismatch. The
     setting opens the film stopped and DISABLES NOTHING — a press after it
     plays, and turning it off again does not restart what it stopped. */
  useIsomorphicLayoutEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const query = window.matchMedia(REDUCED_MOTION_QUERY);

    const onChange = () => {
      if (query.matches) {
        setWanted(false);
      }
    };

    onChange();

    /* ⚠ A MediaQueryList IS NOT AN EventTarget ON SAFARI 13 AND OLDER EMBEDDED
       WEBVIEWS: addEventListener is absent there and calling it throws from a
       committed effect. Remove the pair when browserslist rules those out. */
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", onChange);

      return () => query.removeEventListener("change", onChange);
    }

    query.addListener(onChange);

    return () => query.removeListener(onChange);
  }, []);

  /* THE THREE EVENTS THE BLOCK DID NOT CAUSE. ⚠ BOUND BY HAND on the node this
     side did not render — no handler crosses the boundary — and every one taken
     off on unmount.

     ⚠ DECLARED BEFORE THE PLAYBACK EFFECT, AND AS A LAYOUT EFFECT: layout
     effects run in the order they are written, so this binds before the pause
     the motion correction asks for below. After paint instead, that pause's own
     event can arrive before the listener exists — which leaves `selfPaused`
     set and swallows the next GENUINE interruption. */
  useIsomorphicLayoutEffect(() => {
    const element = mediaFor(hostRef.current);

    if (!element) {
      return undefined;
    }

    const onPause = () => {
      if (selfPaused.current) {
        selfPaused.current = false;

        return;
      }

      setInterrupted(true);
    };

    /* ⚠ THE NAME HAS TO FOLLOW THE ELEMENT, NOT THE INTERRUPTION ALONE. A
       resume the block did not ask for — Chrome's own video context menu, a
       Picture-in-Picture play, a hardware media key — moves a film the control
       still believes is stopped, so the control goes on offering Play, drawing
       the play mark and reporting data-playing="false" over a running film.
       That is a value mismatch (SC 4.1.2), and it takes two presses to stop a
       film the control said was already stopped.

       The claim is what keeps the block's own play() out of that reading, and
       it is spent here rather than anywhere else because exactly one `play`
       event can answer the one call that made it. */
    const onPlay = () => {
      setInterrupted(false);

      if (selfPlayed.current) {
        selfPlayed.current = false;

        return;
      }

      setWanted(true);
    };

    /* Read back into the sound control's name: Chrome's own video context menu
       offers Mute with no `controls` on the element.

       ⚠ AND PUT BACK RATHER THAN READ WHERE THERE IS NO CONTROL TO PUT IT BACK
       WITH, which is the whole of what `hide_mute_control` promises: that same
       menu can unmute a film this block draws no sound control for, leaving a
       reader no way to silence it again. Gated on the element's own answer
       because the correction fires this listener a second time. */
    const onVolumeChange = () => {
      if (!showMute && !element.muted) {
        element.muted = true;

        return;
      }

      setMuted(element.muted);
    };

    element.addEventListener("pause", onPause);
    element.addEventListener("play", onPlay);
    element.addEventListener("volumechange", onVolumeChange);

    /* ⚠ READ ONCE HERE TOO, because a row that LOSES its sound control while the
       film is audible gets no volume change of its own — and AFTER THE BIND, or
       the correction's own event finds no listener to put the block back in
       step with the film. */
    if (!showMute && !element.muted) {
      element.muted = true;
    }

    return () => {
      element.removeEventListener("pause", onPause);
      element.removeEventListener("play", onPlay);
      element.removeEventListener("volumechange", onVolumeChange);
    };
    /* `showMute` is read at bind time and inside the volume listener, so a row
       that gains or loses its sound control has to rebind rather than hold the
       first answer. */
  }, [showMute]);

  /* PLAYBACK, written onto the element the server drew. With no film in scope
     the controls no-op: rendering them on a post-mount query instead would be
     the hydration mismatch again. */
  useIsomorphicLayoutEffect(() => {
    const element = mediaFor(hostRef.current);

    if (!element) {
      return undefined;
    }

    /* ⚠ THE PROPERTY, NOT THE ATTRIBUTE. `muted` as a content attribute sets
       the DEFAULT muted state only, so removing it does not unmute a running
       element. The server writes the attribute, which is what licenses a
       script-free autoplay; the property is this side's. */
    element.muted = muted;

    /* What the sheet may read, and what the server renders neither of — absent
       therefore means "script has not spoken yet", which is one state for the
       sheet to match and no hydration mismatch. */
    const root = element.closest(ROOT);

    if (root) {
      root.setAttribute("data-playing", String(playing));
      root.setAttribute("data-muted", String(muted));
    }

    if (!playing) {
      /* Claimed ONLY WHERE THE ELEMENT IS RUNNING: pause() on an already paused
         one fires nothing at all, so a claim there would swallow the next
         genuine interruption instead. */
      if (!element.paused) {
        selfPaused.current = true;
      }

      element.pause();

      return undefined;
    }

    /* Claimed ONLY WHERE THE ELEMENT IS PAUSED, which is the pause claim above
       read backwards: play() on an already running element fires nothing at
       all, so a claim there would stand until the next call and swallow the
       next genuine resume instead. */
    if (element.paused) {
      selfPlayed.current = true;
    }

    /* ⚠ A `live` FLAG RATHER THAN THE ERROR'S NAME, which is not reliable
       across engines: pause() rejects a play() in flight with an abort, and
       that pause is always this effect's own — so it always arrives after the
       cleanup below. A rejection reaching a LIVE effect is therefore a policy
       refusal, and the control must offer Play rather than lie. Promise.resolve
       because an older engine returns undefined from play(). */
    let live = true;

    Promise.resolve(element.play()).catch(() => {
      if (live) {
        setWanted(false);
      }
    });

    /* ⚠ AND SPENT HERE WHERE THE CALL STARTED NOTHING, because only an event
       spends it otherwise. A policy refusal returns before touching the
       element, so `paused` is still true and no event is coming: a claim left
       standing is spent by the next OUTSIDE resume instead, which leaves the
       control naming Play over a running film and its first press dead. An
       engine that did start the film cleared `paused` in the same task, so its
       queued `play` event is this call's and the claim has to survive — which
       is why the catch above, a microtask later, cannot be where this happens.
       Both guards are needed: the one above refuses to claim a film that was
       ALREADY running, and this one drops a claim the call did not earn. */
    if (element.paused) {
      selfPlayed.current = false;
    }

    return () => {
      live = false;
    };
  }, [muted, playing]);

  /* ⚠ THE PRESS ANSWERS THE NAME ON THE CONTROL, which parts from the last
     answer given once something has interrupted the film: !wanted would offer
     Pause on a block that is not running. */
  const onPlayback = () => {
    setInterrupted(false);
    setWanted(!playing);
  };

  /* Independent of playback, and the click itself IS the gesture that permits
     the unmute. No useCallback on either: neither handler feeds a memoized
     child or an effect, so a stable identity buys nothing. */
  const onSound = () => setMuted(current => !current);

  return (
    /* No role, no name and no group: two named buttons, and a group here is the
       ARIA the first rule forbids. No aria-live either — a press changes the
       FOCUSED control's own name, which a reader announces off the focused
       element, so a region would say it twice. */
    <div className={styles.controls} ref={hostRef}>
      {/* The name carries the state, so there is no `pressed` beside it: a
          control that both renames itself and announces aria-pressed leaves
          "Play, pressed" to be read two contradictory ways, and a pressed
          state of false is a third thing for one control to say. */}
      <Button
        ariaLabel={playing ? PAUSE_LABEL : PLAY_LABEL}
        className={styles.control}
        onClick={onPlayback}
        size="xSmall"
        startIcon={playing ? <PauseGlyph /> : <PlayGlyph />}
        variant="glass"
      />
      {showMute && (
        <Button
          ariaLabel={muted ? UNMUTE_LABEL : MUTE_LABEL}
          className={styles.control}
          onClick={onSound}
          size="xSmall"
          startIcon={muted ? <SoundOnGlyph /> : <SoundOffGlyph />}
          variant="glass"
        />
      )}
    </div>
  );
}
export const MediaControls = withSafeRender(MediaControlsImpl, "MediaControls");

export default MediaControls;
